import moment from 'moment';
import { uniqBy } from 'lodash-es';
import type { CoreTypes } from '../../../core';
import type { OutTypes } from './schedule';
import type { Types as Skal24Types } from '../types';
import type { BaseOptions } from '../../../common/types';
import { makeChainable } from '../../../common/make-chainable';


const separator       = ', ';
const arbetstid       = 'Arbetstid'.toLowerCase();
const firstDay        = '1970-01-05';
const numDays         = 5;
const defaultDayStart = [8,  0];
const defaultDayEnd   = [17, 0];

function generateDate (
  days:    number,
  hours:   number,
  minutes: number
) {
  return moment.utc(firstDay)
    .add(days, 'days')
    .add(hours, 'hours')
    .add(minutes, 'minutes');
}

function startAndEnd (
  lesson: Skal24Types.lesson
): undefined | { start: moment.Moment, end: moment.Moment, parked: false } {
  if ('Length' in lesson && 'DayOfWeek' in lesson && 'StartTime' in lesson) {
    const dateTimeDate = moment(lesson.StartTime, 'MM/DD/YY hh:mm:ss');
    const hours   = dateTimeDate.get('hours');
    const minutes = dateTimeDate.get('minutes');

    // let start = moment.utc(firstDay)
    //                   .add(lesson.DayOfWeek, "days")
    //                   .add(hours, "hours")
    //                   .add(minutes, "minutes");
    const start = generateDate(lesson.DayOfWeek!, hours, minutes);
    const end = start.clone().add(lesson.Length, 'minute');
    return { start: start, end: end, parked: false };
  }

  return;
}

function parseActualWeeks (val?: string) {
  if (!val) return [];

  const weeks: number[] = [];

  val
    .split(',')
    .forEach(x => {
      if (x.includes('-')) {
      // multiple weeks
        const [start, end] = x.split('-');
        for (let i = parseInt(start); i <= parseInt(end); i++)
          weeks.push(i);
      } else {
      // single week
        weeks.push(parseInt(x));
      }
    });

  return weeks;
}

function addGroupsFromName (
  name:           string,
  groupSet:       Set<string>,
  groupNamePkMap: Map<string, string>
) {
  // assumes course names of the type "10IDS01-EK20C-LAG"
  const regex = /-([^\s-]+)/gi;

  let matches: RegExpExecArray | null;
  while ((matches = regex.exec(name)) !== null) {
    if (groupNamePkMap.has(matches[1])) {
      const PK = groupNamePkMap.get(matches[1])!;
      groupSet.add(PK);
    }
  }
}

function getDay (mnt: moment.Moment) {
  return mnt.dayOfYear() - moment.utc(firstDay).dayOfYear();
}

function createDefaultIntervals () {
  const intervals: CoreTypes.interval[] = [];

  for (let d = 0; d < numDays; d++) {
    intervals.push({
      start: generateDate(d, defaultDayStart[0], defaultDayStart[1]),
      end:   generateDate(d,   defaultDayEnd[0],   defaultDayEnd[1])
    });
  }

  return intervals;
}




export default function (
  groups:          Skal24Types.group[],
  lessons:         Skal24Types.lesson[],
  meta:            Skal24Types.options & BaseOptions | undefined,

  groupPlansMap:   Map<number, Set<string> >,
  planMap:         Map<number, Skal24Types.plan>,

  subjectMap:      Map<number, string>,
  planTeachersMap: Map<number, Set<string> >,
  groupClassesMap: Map<string, Set<string> >,
  RSgroups:        OutTypes.group[],

  groupMap:          Map<number, Skal24Types.group>,
  lessonRoomsMap:    Map<number, Set<string> >,
  lessonTeachersMap: Map<number, Set<string> >,
  lessonGroupsMap:   Map<number, Set<string> >,
  RSteachers:        OutTypes.teacher[],

  warnings:       string[],
  errors:         string[]
): { courses: OutTypes.course[], events: OutTypes.event[] } {

  // used when deducing groups from the course name
  const groupNamePkMap = new Map(RSgroups.map(g => [g.displayName!, g.ids!]));

  ////
  //// courses
  ////
  let courseMap: Map<number, OutTypes.course> = new Map();
  groups.forEach(x => {
    // need to be a scheduled group
    if (x.bClass) return;

    // check if at least one plan intersects our period
    const plans = groupPlansMap.get(x.PK);

    // fetch subject via first plan
    let subject: string | undefined;
    if (plans) {
      const subjPK = planMap.get(parseInt([...plans][0]))?.FKSubject;
      if (subjPK) subject = subjectMap.get(subjPK);
    }
    // if (subject) console.log(subject)

    const groupSet = groupClassesMap.get(x.PK.toString()) ?? new Set<string>();

    // attempt to deduce groups by analyzing the course name
    if (meta?.deduceGroupsFromCourseName && x.ID) {
      addGroupsFromName(x.ID, groupSet, groupNamePkMap);
    }



    // if (groups) console.log(groups)

    const teacherSet = new Set<string>();
    groupPlansMap.get(x.PK)?.forEach(p => {
      planTeachersMap.get(parseInt(p))?.forEach(t => teacherSet.add(t));
    });
    // if (teacherSet) console.log(teacherSet)


    courseMap.set(x.PK, {
      ids:         x.PK.toString(),
      displayName: x.ID,

      ...( subject         && { subject                                       } ),
      ...( groupSet?.size  && { groups:   [...groupSet  ].map(to => ({ to })) } ),
      ...( teacherSet.size && { teachers: [...teacherSet].map(to => ({ to })) } )
    });
  });



  ////
  //// events
  ////
  let numAdditionalCourses = 0;
  const eventMap    = new Map<string, OutTypes.event[]>();
  const intervalMap = new Map<string, Map<number, CoreTypes.interval> >();
  lessons.forEach(l => {
    // ignore all lessons which are not scheduled on the week of interest
    if (meta?.week == undefined || !parseActualWeeks(l.CalcActualWeeks).includes(meta.week)) return;


    const coursesIds = [...(lessonGroupsMap.get(l.PK) ?? new Set())]
      .map(id => groupMap.get(parseInt(id)))
      .filter((g): g is NonNullable<typeof g> => g?.bClass == false)
      .sort((g1, g2) => g1.PK - g2.PK)
      .map(g => g.PK);

    const event: OutTypes.event = {
      ids: l.PK.toString(),

      duration:          l.Length,
      preferredDuration: l.Length,
      ...startAndEnd(l)
    };

    // try add room
    const rooms = lessonRoomsMap.get(l.PK);
    if (rooms?.size) {
      event.inLocations = [...rooms];
      event.locations   = [...rooms].map(l => ({ locations: [l] }));
    }

    // try add teachers
    const teachers = lessonTeachersMap.get(l.PK);
    if (teachers?.size) event.teachers = [...teachers].map(to => ({ to }));

    // fetch groups
    const classes: Set<string> = new Set();
    lessonGroupsMap.get(l.PK)?.forEach(scheduleGroupId => {
      groupClassesMap.get(scheduleGroupId)?.forEach(classId => {
        classes.add(classId);
      });
    });

    // attempt to deduce groups by analyzing the course name
    // (but only if the event explicitly has groups or its course has none)
    const courseHasGroups = coursesIds.some(x => courseMap.get(x)?.groups);
    if (meta?.deduceGroupsFromCourseName && (!courseHasGroups || classes.size)) {
      coursesIds.map(x => courseMap.get(x)?.displayName)
        .filter(x => x != null)
        .forEach(x => addGroupsFromName(x, classes, groupNamePkMap));
    }

    // try add groups
    if (classes.size) event.groups = [...classes].map(to => ({ to }));


    const subject = subjectMap.get(l.FKSubject!);
    if (subject?.toLowerCase() == arbetstid) {
      // is an interval

      // ensure start and end is set
      const start = event.start;
      const end   = event.end;
      if (!start || !end) {
        warnings.push('Start or end not set on interval event');
        return;
      }

      // interval day
      const day = getDay(moment.utc(start));

      if (0 > day || day > numDays - 1) {
        warnings.push('Interval day out of bounds');
        return;
      }

      // add interval
      teachers?.forEach(t => {
        if (!intervalMap.has(t)) intervalMap.set(t, new Map());
        intervalMap.get(t)?.set(day, { start, end });
      });

    } else {
      // is an event

      ////
      //// if there is no course, create it
      //// (can also be interval)
      ////
      if (!coursesIds.length) {
        const id = --numAdditionalCourses;

        courseMap.set(id, {
          ids: id.toString(),

          ...( subject    && { displayName: subject } ),
          ...( subject    && { subject              } ),

          // // makes it easier to se in the table
          // ...( teachers?.size && { teachers: [...teachers] } ),
          // ...( classes?.size  && { groups:   [...classes]  } ),
        });

        coursesIds.push(id);
      }


      // add
      const key = coursesIds.join(separator);
      if (!eventMap.has(key)) eventMap.set(key, []);
      eventMap.get(key)?.push(event);
    }

  });


  ////
  //// set teacher days and intervals
  ////
  intervalMap.forEach((v, k) => {
    // fetch teacher
    const teacher = RSteachers.find(t => t.ids == k);
    if (!teacher) {
      warnings.push(`An interval referred to the unknown teacher with PK = ${k}`);
      return;
    }

    // construct
    const intervals = createDefaultIntervals();
    const days: CoreTypes.day[] = [];
    v.forEach((i, d) => {
      days.push({ day: d });
      intervals[d] = i;
    });

    // set
    // (set days only if not all are permitted)
    teacher.intervals = intervals;
    if (days.length != numDays) teacher.days = days;
  });


  ////
  //// merge courses
  ////
  eventMap.forEach((events, key) => {
    const courseIds = key.split(separator).map(x => parseInt(x));

    if (courseIds.length > 1) {
      const idss:         string[] = [];
      const displayNames: string[] = [];

      const subjects = new Set<string>();
      const groups   = new Set<string>();
      const teachers = new Set<string>();

      // merge courses
      courseIds.map(x => courseMap.get(x))
        .filter(c => c != undefined)
        .forEach(c => {
          if (c.ids) idss.push(c.ids);
          if (c.displayName) displayNames.push(c.displayName);

          if (c.subject) subjects.add(c.subject);
          if (c.groups) c.groups.forEach(({ to })   => groups.add(to.toString()));
          if (c.teachers) c.teachers.forEach(({ to })  => teachers.add(to.toString()));
        });

      const newKey = --numAdditionalCourses;
      const newCourse: OutTypes.course = {
        ids:         idss.join(separator),
        displayName: displayNames.join(separator),

        ...( subjects.size && { subject:  [...subjects].join(separator)     } ),

        ...( groups.size   && { groups:   [...groups].map(to => ({ to }))   } ),
        ...( teachers.size && { teachers: [...teachers].map(to => ({ to })) } )
      };

      // add courses
      // (leave old courses if they have events)        <-- WTF!?
      courseMap.set(newKey, newCourse);

      // link events to new course
      eventMap.set(newKey.toString(), [...events]);
      events.length = 0;

      // console.log(`The courses with PKs [${courseIds.join(separator)}] needed to be merged.`);
      warnings.push(`The courses with PKs [${courseIds.join(separator)}] needed to be merged.`);
    }
  });



  ////
  //// link events with courses
  ////
  const populatedCourses = new Set<number>();
  eventMap.forEach((events, key) => {
    // need to contain events
    if (!events.length) return;

    const courseIds = key.split(separator).map(x => parseInt(x));

    if (courseIds.length != 1 && events.length) throw new Error('Well this was unexpected...');

    const courseId = courseIds[0];
    const PK       = courseMap.get(courseId)?.ids;

    if (PK == undefined) {
      // console.log(courseIds, PK, key, events)
      // throw "Well this was also unexpected...";
      // console.log(`The events with PKs ${events.map(e => e.ids)} were ignored since their course was not found.`);
      errors.push(`The events with PKs ${events.map(e => e.ids).join(', ')} were ignored since their course was not found.`);
      events.length = 0;
      return;
    }

    // link
    events.forEach(e => e.course = PK);

    populatedCourses.add(courseId);
  });



  ////
  //// try remove empty courses
  ////
  if (meta?.ignoreEmptyCourses) {
    // filter in place
    const courses = [...courseMap].filter(([k]) => populatedCourses.has(k));
    courseMap = new Map(courses);
  }


  ////
  //// try promote groups, teachers and locations to the parent course
  //// (if the course has none and all sibling events have the same)
  ////
  const keys = ['groups', 'teachers', 'locations'] as const;
  for (const key of keys) {
    courseMap.forEach((c, courseKey) => {
      // need to use the "courseKey" as it is different from the "c.ids" if the course has been merged
      const events = eventMap.get(courseKey.toString());

      const eventEntries = makeChainable(events ?? [])
        .chain(
          x => x
            .map(e => e[key] ?? c[key])
            .filter(x => x != null),
          x => uniqBy(x, x => x
            .map(x => 'to' in x ? x.to : x.locations[0])
            .sort()
            .join('.')
          )
        )
        .value;

      // the event entries must be exactly one
      if (eventEntries.length != 1) return;

      // update
      switch (key) {
        case 'groups':    c[key] = eventEntries[0] as (typeof c)[typeof key]; break;
        case 'teachers':  c[key] = eventEntries[0] as (typeof c)[typeof key]; break;
        case 'locations': c[key] = eventEntries[0] as (typeof c)[typeof key]; break;
        default: throw new Error(`Unknown key "${key satisfies never as string}"`);
      }
      events?.forEach(e => delete e[key]);
    });
  }


  // let eventsArray: Types.courseEvent[] = [];
  return { courses: [...courseMap.values()], events: [...eventMap.values()].flat() };
}
